import { useCallback, useRef } from 'react';

import datetime, { atMSK } from '@sayyesschool/common/libs/datetime';
import ContentEditor from '@sayyesschool/frontend/components/content-editor';
import ImageField from '@sayyesschool/frontend/components/image-field';
import UserSelect from '@sayyesschool/frontend/components/user-select';
import { levelOptions } from '@sayyesschool/frontend/data/common';
import { meetingRouteSchemaOptions, meetingStatusOptions } from '@sayyesschool/frontend/features/meetings';
import { useFormData } from '@sayyesschool/frontend/hooks/form';
import { Flex, Form, Grid, Surface } from '@sayyesschool/frontend/ui-components';

const getFormData = ({
    title = '',
    startDate = new Date(),
    duration = 50,
    level = '',
    status = 'scheduled',
    hostId,
    online = false,
    free = false,
    published = false,
    image = {},
    description = '',
    materialsUrl = '',
    routeSchema = ''
} = {}) => ({
    title,
    startDate: datetime(startDate).format('YYYY-MM-DDTHH:mm'),
    level: level?.toString(),
    duration,
    status,
    hostId,
    online,
    free,
    published,
    image,
    description,
    materialsUrl,
    routeSchema
});

export default function MeetingForm({
    meeting,
    hosts = [],
    onSubmit,
    ...props
}) {
    const { data, setData, handleChange } = useFormData(
        getFormData(meeting),
        [meeting?.id]
    );

    const fileInputRef = useRef();
    const contentEditorRef = useRef();

    const handleSubmit = useCallback(() => {
        const file = fileInputRef.current.file;
        const content = contentEditorRef.current.getData();

        if (file && !data.file?.delete) {
            file.path = 'meetings';
            data.file = file;
            data.image = data.image || {};
        }

        if (data.online) {
            data.routeSchema = undefined;
        }

        const startDate = datetime(data.startDate).toDate();
        const endDate = datetime(data.startDate).add(data.duration, 'minutes').toDate();

        onSubmit({
            ...data,
            startDate,
            endDate,
            description: content
        });

        fileInputRef.current.reset();
    }, [data, onSubmit]);

    const handleDeleteImage = useCallback(image => {
        setData(data => ({
            ...data,
            image: null,
            file: {
                url: image.url,
                delete: true
            }
        }));

        return Promise.resolve();
    }, [setData]);

    return (
        <Form
            onSubmit={handleSubmit}
            {...props}
        >
            <Grid gap="m">
                <Grid.Item
                    lg={4}
                    md={4}
                    sm={12}
                >
                    <Flex
                        gap="s"
                        column
                    >
                        <Form.Input
                            label="Тема"
                            name="title"
                            value={data.title}
                            required
                            onChange={handleChange}
                        />

                        <Form.Input
                            label="Когда"
                            type="datetime-local"
                            name="startDate"
                            value={data.startDate}
                            message={`Московское время: ${atMSK(data.startDate).format('HH:mm')}`}
                            required
                            onChange={handleChange}
                        />

                        <Form.Input
                            label="Продолжительность"
                            type="number"
                            name="duration"
                            value={data.duration}
                            required
                            onChange={handleChange}
                        />

                        <UserSelect
                            label="Ведущий"
                            name="hostId"
                            value={data.hostId}
                            options={hosts?.map(host => ({
                                key: host.id,
                                value: host.id,
                                label: host.fullname
                            }))}
                            clearOnBlur
                            required
                            onChange={handleChange}
                        />

                        <Form.Select
                            label="Уровень"
                            name="level"
                            value={data.level}
                            options={levelOptions}
                            onChange={handleChange}
                        />

                        <Form.Select
                            label="Статус"
                            name="status"
                            value={data.status}
                            options={meetingStatusOptions}
                            onChange={handleChange}
                        />

                        {!data.online && (
                            <Form.Select
                                label="Схема проезда"
                                name="routeSchema"
                                value={data.routeSchema}
                                options={meetingRouteSchemaOptions}
                                onChange={handleChange}
                            />
                        )}

                        {!meeting?.id &&
                            <Form.Switch
                                label="Онлайн"
                                name="online"
                                checked={data.online}
                                disabled={data.id}
                                onChange={handleChange}
                            />
                        }

                        <Form.Switch
                            label="Бесплатно"
                            name="free"
                            checked={data.free}
                            onChange={handleChange}
                        />

                        <Form.Switch
                            label="Опубликована"
                            name="published"
                            checked={data.published}
                            onChange={handleChange}
                        />
                    </Flex>
                </Grid.Item>

                <Grid.Item
                    lg={8}
                    md={8}
                    sm={12}
                >
                    <Flex
                        gap="m"
                        column
                    >
                        <ImageField
                            ref={fileInputRef}
                            name="file"
                            label="Изображение"
                            image={data.image}
                            cropperOptions={{
                                aspectRatio: 16 / 9
                            }}
                            onDelete={handleDeleteImage}
                        />

                        <Form.Input
                            type="url"
                            name="materialsUrl"
                            value={data.materialsUrl}
                            label="Ссылка на материалы"
                            onChange={handleChange}
                        />

                        <Form.Field label="Описание">
                            <Surface
                                radius="xs"
                                variant="outlined"
                            >
                                <ContentEditor
                                    ref={contentEditorRef}
                                    content={data.description ?? ''}
                                />
                            </Surface>
                        </Form.Field>
                    </Flex>
                </Grid.Item>
            </Grid>
        </Form>
    );
}